import * as React from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";

import { cn } from "../../lib/utils";
import { SPARKLINE_LINE } from "./chart-theme";

/**
 * Sparkline — minimal inline trend line for stat-card KPIs (issue #234,
 * DESIGN-SPEC.md §4.2/§4.5).
 *
 * Spec: "Optional 96×34 sparkline top-right" (§4.2); "Sparklines: 1.5px, no
 * axes, no fill" (§4.5). Built on Recharts (kept per §8, not a hand-rolled
 * SVG) so it inherits the same data contract as every other chart in the
 * app — `data` is an array of `{ value: number }` (or objects with a custom
 * `dataKey`).
 *
 * Color defaults to the shared chart accent but stat-card callers pass an
 * explicit `color` for signed-P&L colorization (positive/negative/neutral)
 * per §4.2's "Value color: --text unless the metric itself is a signed P&L".
 */
export const Sparkline = React.forwardRef(function Sparkline(
  {
    data,
    dataKey = "value",
    color = "var(--accent, var(--color-primary-500))",
    width = 96,
    height = 34,
    className,
    "aria-label": ariaLabel,
    ...props
  },
  ref
) {
  const hasData = Array.isArray(data) && data.length > 1;

  return (
    <div
      ref={ref}
      className={cn("shrink-0", className)}
      style={{ width, height }}
      role="img"
      aria-label={ariaLabel || "Trend sparkline"}
      {...props}
    >
      {hasData && (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 2, right: 1, left: 1, bottom: 2 }}>
            <Line
              type="linear"
              dataKey={dataKey}
              stroke={color}
              {...SPARKLINE_LINE}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
});
Sparkline.displayName = "Sparkline";

export default Sparkline;
